import type { Lang, SiteCopy } from './i18n/types';
import type { SeoMeta } from './seo/keywords';
import { resolveOgImage } from './seo/image-url';

export type SeoPage = 'home' | 'unite-base' | 'unite-go';

export type SeoOptions = {
  canonicalPaths?: { en: string; zh: string };
  ogImage?: string;
  ogType?: 'website' | 'article';
  article?: { publishedAt?: string | null; modifiedAt?: string | null };
};

export function getSiteOrigin(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

export function htmlLangFor(lang: Lang): string {
  return lang === 'zh' ? 'zh-CN' : 'en';
}

export function pagePath(page: SeoPage, lang: Lang): string {
  if (page === 'unite-base') {
    return lang === 'zh' ? '/zh/solutions/unite-base' : '/solutions/unite-base';
  }
  if (page === 'unite-go') {
    return lang === 'zh' ? '/zh/solutions/unite-go' : '/solutions/unite-go';
  }
  return lang === 'zh' ? '/?lang=zh' : '/';
}

export function pageUrl(lang: Lang, origin = getSiteOrigin(), page: SeoPage = 'home'): string {
  return `${origin}${pagePath(page, lang)}`;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  const sel = `meta[${attr}="${key}"]`;
  let el = document.querySelector<HTMLMetaElement>(sel);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, hreflang?: string): void {
  const sel = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.querySelector<HTMLLinkElement>(sel);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(id: string, data: Record<string, unknown>): void {
  const sel = `script[type="application/ld+json"]#${id}`;
  let el = document.querySelector<HTMLScriptElement>(sel);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function applyPageSeo(
  lang: Lang,
  copy: SiteCopy,
  page: SeoPage = 'home',
  metaOverride?: SeoMeta,
  options?: SeoOptions,
): void {
  const origin = getSiteOrigin();
  if (!origin) return;

  const meta = metaOverride ?? copy.meta;
  const enPath = options?.canonicalPaths?.en ?? pagePath(page, 'en');
  const zhPath = options?.canonicalPaths?.zh ?? pagePath(page, 'zh');
  const canonical = `${origin}${lang === 'zh' ? zhPath : enPath}`;
  const ogLocale = lang === 'zh' ? 'zh_CN' : 'en_US';
  const ogImage = options?.ogImage ?? resolveOgImage(origin, metaOverride?.og_image);
  const ogType = options?.ogType ?? 'website';

  document.documentElement.lang = htmlLangFor(lang);
  document.title = meta.title;

  upsertMeta('name', 'description', meta.description);
  if (meta.keywords) upsertMeta('name', 'keywords', meta.keywords);
  upsertMeta('name', 'robots', 'index, follow, max-image-preview:large');
  upsertMeta('name', 'googlebot', 'index, follow, max-image-preview:large');
  upsertMeta('name', 'bingbot', 'index, follow, max-image-preview:large');

  upsertMeta('property', 'og:type', ogType);
  upsertMeta('property', 'og:site_name', 'UniteStream');
  upsertMeta('property', 'og:title', meta.title);
  upsertMeta('property', 'og:description', meta.description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:locale', ogLocale);
  upsertMeta('property', 'og:locale:alternate', lang === 'zh' ? 'en_US' : 'zh_CN');
  upsertMeta('property', 'og:image', ogImage);
  upsertMeta('property', 'og:image:alt', meta.title);
  upsertMeta('property', 'og:image:width', '1200');
  upsertMeta('property', 'og:image:height', '630');

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', meta.title);
  upsertMeta('name', 'twitter:description', meta.description);
  upsertMeta('name', 'twitter:image', ogImage);

  upsertLink('canonical', canonical);
  upsertLink('alternate', `${origin}${zhPath}`, 'zh-CN');
  upsertLink('alternate', `${origin}${enPath}`, 'en');
  upsertLink('alternate', `${origin}${enPath}`, 'x-default');

  upsertJsonLd('schema-website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'UniteStream',
    url: origin,
    inLanguage: [htmlLangFor('zh'), htmlLangFor('en')],
    description: meta.description,
  });

  upsertJsonLd('schema-org', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'UniteStream',
    url: origin,
    logo: ogImage,
  });

  if (ogType === 'article' && options?.article) {
    upsertJsonLd('schema-article', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: meta.title,
      description: meta.description,
      image: ogImage,
      datePublished: options.article.publishedAt ?? undefined,
      dateModified: options.article.modifiedAt ?? options.article.publishedAt ?? undefined,
      author: { '@type': 'Organization', name: 'UniteStream' },
      publisher: {
        '@type': 'Organization',
        name: 'UniteStream',
        logo: { '@type': 'ImageObject', url: `${origin}/images/logo.png` },
      },
      inLanguage: htmlLangFor(lang),
      mainEntityOfPage: canonical,
    });
    document.getElementById('schema-software')?.remove();
  } else {
    document.getElementById('schema-article')?.remove();
    upsertJsonLd('schema-software', {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'UniteStream',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: canonical,
      description: meta.description,
      keywords: meta.keywords,
      inLanguage: htmlLangFor(lang),
      featureList: [
        lang === 'zh' ? '机器人数据采集' : 'Robot data acquisition',
        lang === 'zh' ? '机器人数据分析' : 'Robot data analytics',
        lang === 'zh' ? '机器人数据治理' : 'Robot data governance',
        lang === 'zh' ? '运动回放与仿真' : 'Motion replay and simulation',
      ],
    });
  }
}
